import { GENERATION_IR_VERSION } from "./contracts.js";
import { getDiscreteIoCompletionMode } from "./discrete-io-completion.js";

function numOrZero(value) {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
}

function toStIdentifier(value, fallback) {
    const base = String(value || "")
        .replace(/[^A-Za-z0-9_]+/g, "_")
        .replace(/^_+|_+$/g, "");
    const normalized = base || fallback;
    return /^[A-Za-z_]/.test(normalized) ? normalized : "_" + normalized;
}

function toStableStIdentifier(value, fallback, minCoreLength = 3) {
    const raw = String(value || "");
    const candidate = toStIdentifier(raw, fallback);
    const candidateCore = candidate.replace(/_/g, "");
    const rawHasNonAscii = /[^\x00-\x7F]/.test(raw);
    if (rawHasNonAscii && candidateCore.length < minCoreLength) {
        return toStIdentifier(fallback, fallback);
    }
    return candidate;
}

function asArray(value) {
    return Array.isArray(value) ? value : [];
}

function buildMotionLookup(context) {
    const byMotionId = new Map();
    const byNodeKey = new Map();
    const axes = asArray(context?.axes);
    for (const axis of axes) {
        for (const motion of asArray(axis?.motions)) {
            if (motion?.id) byMotionId.set(motion.id, { axis, motion });
            if (axis?.name && motion?.name) {
                byNodeKey.set(`${axis.name}::${motion.name}`, { axis, motion });
            }
        }
    }
    return {
        byMotionId,
        byNodeKey
    };
}

function normalizePortOrder(portName) {
    if (portName === "output_1") return 1;
    if (portName === "output_2") return 2;
    if (portName === "output_3") return 3;
    return 99;
}

function getPrimaryTarget(connectionsByFrom, nodeId, outputName = "output_1") {
    const edge = (connectionsByFrom.get(nodeId) || []).find((item) => item.output === outputName);
    return edge?.to || null;
}

function buildGraphMaps(context) {
    const flowNodes = asArray(context?.flowNodes);
    const connections = asArray(context?.connections)
        .map((connection) => ({
            from: String(connection?.from || ""),
            to: String(connection?.to || ""),
            output: String(connection?.output || "output_1"),
            input: String(connection?.input || "input_1")
        }))
        .filter((connection) => connection.from && connection.to);

    const nodesById = new Map(flowNodes.map((node) => [String(node.id), node]));
    const connectionsByFrom = new Map();
    const connectionsByTo = new Map();
    for (const connection of connections) {
        if (!connectionsByFrom.has(connection.from)) connectionsByFrom.set(connection.from, []);
        connectionsByFrom.get(connection.from).push(connection);
        if (!connectionsByTo.has(connection.to)) connectionsByTo.set(connection.to, []);
        connectionsByTo.get(connection.to).push(connection);
    }

    for (const list of connectionsByFrom.values()) {
        list.sort((a, b) => {
            const byPort = normalizePortOrder(a.output) - normalizePortOrder(b.output);
            if (byPort !== 0) return byPort;
            return String(a.to).localeCompare(String(b.to));
        });
    }

    return {
        nodesById,
        connections,
        connectionsByFrom,
        connectionsByTo
    };
}

function enumerateReachableNodeIds(startId, connectionsByFrom) {
    const reachable = new Set();
    const queue = startId ? [startId] : [];
    while (queue.length) {
        const nodeId = queue.shift();
        if (!nodeId || reachable.has(nodeId)) continue;
        reachable.add(nodeId);
        for (const connection of connectionsByFrom.get(nodeId) || []) {
            if (!reachable.has(connection.to)) queue.push(connection.to);
        }
    }
    return reachable;
}

function branchKeyFromOutput(outputName) {
    if (outputName === "output_1") return "true";
    if (outputName === "output_2") return "false";
    if (outputName === "output_3") return "timeout";
    return outputName || "next";
}

function buildConditionExpression(node, stepNumber) {
    const conditions = asArray(node?.conditions);
    if (!conditions.length) {
        return {
            expression: `Cond${stepNumber}`,
            terms: []
        };
    }
    const terms = conditions.map((entry, index) => {
        const raw = String(entry?.expression || entry?.label || "").trim();
        return {
            raw,
            ident: toStableStIdentifier(
                raw || entry?.label || node?.conditionText || `Cond${stepNumber}_${index + 1}`,
                `Cond${stepNumber}_${index + 1}`,
                3
            )
        };
    });
    const joiner = node?.operator === "OR" ? " OR " : " AND ";
    return {
        expression: terms.map((item) => item.ident).join(joiner) || `Cond${stepNumber}`,
        terms
    };
}

function buildButtonExpression(node, stepNumber) {
    const signalName = String(node?.signalName || "").trim();
    const baseName = signalName || node?.label || node?.name || `Button${stepNumber}`;
    const mode = node?.mode === "alternate" ? "alternate" : "momentary";
    return {
        mode,
        signalIdent: toStableStIdentifier(baseName, `Button${stepNumber}_Input`, 4),
        edgeIdent: mode === "alternate"
            ? toStableStIdentifier(`${baseName}_Rise`, `Button${stepNumber}_Rise`, 5)
            : "",
        latchIdent: mode === "alternate"
            ? toStableStIdentifier(`${baseName}_State`, `Button${stepNumber}_State`, 5)
            : ""
    };
}

function enrichMotionNode(node, motionLookup) {
    const hit =
        motionLookup.byMotionId.get(node.motionId) ||
        motionLookup.byNodeKey.get(`${node.axisName || ""}::${node.motionName || ""}`) ||
        null;
    return {
        ...node,
        axisName: node.axisName || hit?.axis?.name || "",
        motionName: node.motionName || hit?.motion?.name || "",
        motionRef: hit
            ? {
                  axis: hit.axis,
                  motion: hit.motion
              }
            : null
    };
}

function buildTransitionsForNode(node, connectionsByFrom, stepByNodeId) {
    const edges = connectionsByFrom.get(node.id) || [];
    if (node.type === "condition") {
        return {
            true: stepByNodeId.get(getPrimaryTarget(connectionsByFrom, node.id, "output_1")) ?? null,
            false: stepByNodeId.get(getPrimaryTarget(connectionsByFrom, node.id, "output_2")) ?? null,
            timeout: stepByNodeId.get(getPrimaryTarget(connectionsByFrom, node.id, "output_3")) ?? null
        };
    }
    if (node.type === "button") {
        return {
            true: stepByNodeId.get(getPrimaryTarget(connectionsByFrom, node.id, "output_1")) ?? null,
            false: stepByNodeId.get(getPrimaryTarget(connectionsByFrom, node.id, "output_2")) ?? null
        };
    }
    if (node.type === "motion") {
        return {
            next: stepByNodeId.get(getPrimaryTarget(connectionsByFrom, node.id, "output_1")) ?? null,
            timeout: stepByNodeId.get(getPrimaryTarget(connectionsByFrom, node.id, "output_3")) ?? null
        };
    }
    if (node.type === "error") {
        return {};
    }
    if (node.type === "end") {
        const nextEdge = edges[0];
        return nextEdge ? { next: stepByNodeId.get(nextEdge.to) ?? null } : {};
    }
    const nextEdge = edges[0];
    return {
        next: nextEdge ? stepByNodeId.get(nextEdge.to) ?? null : null
    };
}

export function nextGenerationStepNumber(index) {
    return (index + 1) * 10;
}

export function parseStartConditionSpec(text) {
    const raw = String(text || "").trim();
    if (!raw) return null;
    const parts = raw.split("/").map(part => part.trim()).filter(Boolean);
    const tail = parts[parts.length - 1] || raw;
    const match = tail.match(/^(.*)\[([^\]]+)\]$/);
    if (!match) {
        return {
            raw,
            conditionName: tail,
            branchLabel: "",
            placeholder: "",
            includesStart: parts.some(part => part === "開始")
        };
    }
    return {
        raw,
        conditionName: match[1].trim(),
        branchLabel: match[2].trim(),
        placeholder: "",
        includesStart: parts.some(part => part === "開始")
    };
}

export function buildConditionPlaceholder(spec, fallbackIndex, conditionIndex = null) {
    if (!spec || !spec.conditionName || !spec.branchLabel) return "";
    const branchKey = toStableStIdentifier(spec.branchLabel, `Branch${fallbackIndex}`, 2);
    const conditionKey = conditionIndex != null ? `Cond${conditionIndex}` : `Cond${fallbackIndex}`;
    return toStableStIdentifier(`${spec.conditionName}_${spec.branchLabel}`, `${conditionKey}_${branchKey}`, 5);
}

export function buildConditionIndexMap(conditions) {
    return new Map((conditions || []).map((condition, index) => [condition.conditionText, index + 1]));
}

export function resolveMatchedCondition(context, conditionSpec) {
    if (!conditionSpec?.conditionName) return null;
    return (context.conditions || []).find(cond => cond.conditionText === conditionSpec.conditionName) || null;
}

function flattenFlowGraphs(context) {
    const expansionFindings = [];
    const modulesByName = new Map(
        asArray(context?.modules).map((m) => [m.moduleName, m])
    );
    const subprocessById = new Map(
        asArray(context?.subprocesses).map((item) => [String(item?.id || ""), item])
    );

    // 起点となるHomeノードとHomeコネクションを複製
    const rootNodes = asArray(context?.flowNodes).map(n => ({
        ...n,
        _origId: n.id,
        nodePath: `Home:${n.id}`
    }));
    const rootConns = asArray(context?.connections).map(c => ({ ...c }));

    let workNodes = [...rootNodes];
    let workConns = [...rootConns];

    const MAX_DEPTH = 10;

    function expandRecursive(currentNode, parentPath, visited, depth) {
        if (depth > MAX_DEPTH) {
            expansionFindings.push({
                severity: "error",
                code: "FC_SUBPROCESS_RECURSIVE_LIMIT",
                message: `Subprocess recursion limit exceeded at depth ${depth}.`,
                path: parentPath
            });
            return null;
        }

        const subprocessId = String(currentNode.subprocessId || "");
        const definition = subprocessById.get(subprocessId);
        if (!definition) {
            expansionFindings.push({
                severity: "error",
                code: "FC_SUBPROCESS_REF_NOT_FOUND",
                message: `Subprocess node references unknown subprocessId "${subprocessId}".`,
                path: parentPath
            });
            return null;
        }

        const moduleName = definition.moduleName || "";
        if (!moduleName) {
            expansionFindings.push({
                severity: "error",
                code: "FC_SUBPROCESS_MODULE_NOT_FOUND",
                message: `Subprocess "${definition.name}" is missing a moduleName.`,
                path: parentPath
            });
            return null;
        }

        if (visited.has(moduleName)) {
            expansionFindings.push({
                severity: "error",
                code: "FC_SUBPROCESS_CYCLIC_REFERENCE",
                message: `Cyclic subprocess reference detected: ${[...visited, moduleName].join(" -> ")}.`,
                path: parentPath
            });
            return null;
        }

        const moduleData = modulesByName.get(moduleName);
        if (!moduleData) {
            expansionFindings.push({
                severity: "error",
                code: "FC_SUBPROCESS_MODULE_NOT_FOUND",
                message: `Drawflow module "${moduleName}" not found for subprocess "${definition.name}".`,
                path: parentPath
            });
            return null;
        }

        const childFlowNodes = moduleData.flowNodes || [];
        const childConnections = moduleData.connections || [];

        if (childFlowNodes.length === 0) {
            expansionFindings.push({
                severity: "error",
                code: "FC_SUBPROCESS_MODULE_EMPTY",
                message: `Subprocess module "${moduleName}" is empty.`,
                path: parentPath
            });
            return null;
        }

        const startNodes = childFlowNodes.filter(n => n.type === "start");
        if (startNodes.length === 0) {
            expansionFindings.push({
                severity: "error",
                code: "FC_SUBPROCESS_MODULE_NO_START",
                message: `Subprocess module "${moduleName}" has no start node.`,
                path: parentPath
            });
            return null;
        }
        if (startNodes.length > 1) {
            expansionFindings.push({
                severity: "error",
                code: "FC_SUBPROCESS_MODULE_MULTIPLE_START",
                message: `Subprocess module "${moduleName}" has multiple start nodes (${startNodes.length}).`,
                path: parentPath
            });
            return null;
        }

        const endNodes = childFlowNodes.filter(n => n.type === "end");
        if (endNodes.length === 0) {
            expansionFindings.push({
                severity: "error",
                code: "FC_SUBPROCESS_MODULE_NO_END",
                message: `Subprocess module "${moduleName}" has no end node.`,
                path: parentPath
            });
            return null;
        }

        // single-entry / single-exit 制約
        const outEdges = workConns.filter(c => c.from === currentNode.id);
        const hasUnsupportedPorts = outEdges.some(c => c.output !== "output_1");
        if (hasUnsupportedPorts) {
            expansionFindings.push({
                severity: "error",
                code: "FC_SUBPROCESS_MULTIPLE_OUTPUTS_UNSUPPORTED",
                message: `Subprocess node has unsupported multiple output ports. Only single "output_1" is supported.`,
                path: parentPath
            });
            return null;
        }

        const currentVisited = new Set(visited);
        currentVisited.add(moduleName);

        // 子モジュールのノードをプレフィックス化してコピー
        const prefix = `${currentNode.id}`;
        const newNodes = [];
        const newConns = [];

        for (const childNode of childFlowNodes) {
            const nextPath = `${parentPath}/${moduleName}:${childNode.id}`;
            const copiedNode = {
                ...childNode,
                id: `${prefix}_${childNode.id}`,
                _origId: childNode.id,
                nodePath: nextPath
            };

            // もしこの子ノード自体が subprocess の場合は、さらに再帰展開を行う
            if (copiedNode.type === "subprocess" && copiedNode.timingMode !== "fixed_duration") {
                const subResult = expandRecursive(copiedNode, nextPath, currentVisited, depth + 1);
                if (subResult) {
                    newNodes.push(...subResult.nodes);
                    newConns.push(...subResult.connections);
                    // 子モジュールの start と end の繋ぎ込み
                    // 1. copiedNode へ入るコネクションを子の start ノードへ
                    const internalChildConns = childConnections.filter(c => c.to === childNode.id);
                    for (const conn of internalChildConns) {
                        newConns.push({
                            from: `${prefix}_${conn.from}`,
                            to: subResult.startId,
                            output: conn.output || "output_1",
                            input: conn.input || "input_1"
                        });
                    }

                    // 2. 子の end から親の次へ
                    const internalChildConnsOut = childConnections.filter(c => c.from === childNode.id);
                    for (const outEdge of internalChildConnsOut) {
                        for (const endId of subResult.endIds) {
                            const endNode = subResult.nodes.find(n => n.id === endId);
                            if (endNode) {
                                if (!endNode.outputs) endNode.outputs = [];
                                if (!endNode.outputs.includes("output_1")) endNode.outputs.push("output_1");
                            }
                            newConns.push({
                                from: endId,
                                to: `${prefix}_${outEdge.to}`,
                                output: "output_1",
                                input: outEdge.input || "input_1"
                            });
                        }
                    }
                } else {
                    // 展開に失敗した場合は、タイマーへ暗黙フォールバックせず完全に除去します。
                }
            } else {
                newNodes.push(copiedNode);
            }
        }

        // 子モジュール内部のコネクションをコピー & ID書換え（すでに展開処理されたものは除く）
        for (const conn of childConnections) {
            // 親の subprocess 自体に関わるコネクションは expandRecursive の外で繋ぎ替えるため、ここでは無視
            if (conn.from !== startNodes[0].id && !childFlowNodes.some(n => n.id === conn.from && n.type === "subprocess")) {
                // 通常コネクション
                if (conn.to !== startNodes[0].id && !childFlowNodes.some(n => n.id === conn.to && n.type === "subprocess")) {
                    newConns.push({
                        from: `${prefix}_${conn.from}`,
                        to: `${prefix}_${conn.to}`,
                        output: conn.output || "output_1",
                        input: conn.input || "input_1"
                    });
                }
            }
            // startノードから出るコネクションはここで追加
            if (conn.from === startNodes[0].id) {
                newConns.push({
                    from: `${prefix}_${conn.from}`,
                    to: `${prefix}_${conn.to}`,
                    output: conn.output || "output_1",
                    input: conn.input || "input_1"
                });
            }
        }

        return {
            nodes: newNodes,
            connections: newConns,
            startId: `${prefix}_${startNodes[0].id}`,
            endIds: endNodes.map(e => `${prefix}_${e.id}`)
        };
    }

    let iteration = 0;
    while (iteration < 100) {
        const subNode = workNodes.find(n => n.type === "subprocess" && n.timingMode !== "fixed_duration");
        if (!subNode) break;

        iteration++;
        const parentPath = subNode.nodePath || `Home:${subNode.id}`;
        const visited = new Set(["Home"]);
        const result = expandRecursive(subNode, parentPath, visited, 1);

        if (result) {
            workNodes = workNodes.filter(n => n.id !== subNode.id);
            workNodes.push(...result.nodes);

            // コネクションの書き換え
            // 1. 親グラフで subNode へ入るコネクションを、子の start ノードへ繋ぐ
            workConns = workConns.map(c => {
                if (c.to === subNode.id) {
                    return { ...c, to: result.startId };
                }
                return c;
            });

            // 2. 子モジュール内部のコネクションを追加
            workConns.push(...result.connections);

            // 3. 子モジュールから出るコネクション
            const outEdges = workConns.filter(c => c.from === subNode.id);
            workConns = workConns.filter(c => c.from !== subNode.id);

            for (const outEdge of outEdges) {
                for (const endId of result.endIds) {
                    const endNode = workNodes.find(n => n.id === endId);
                    if (endNode) {
                        if (!endNode.outputs) endNode.outputs = [];
                        if (!endNode.outputs.includes("output_1")) endNode.outputs.push("output_1");
                    }
                    workConns.push({
                        from: endId,
                        to: outEdge.to,
                        output: "output_1",
                        input: outEdge.input || "input_1"
                    });
                }
            }
        } else {
            // 展開に失敗した場合は、タイマーへ暗黙フォールバックせず完全に除去します。
            workNodes = workNodes.filter(n => n.id !== subNode.id);
            workConns = workConns.filter(c => c.to !== subNode.id && c.from !== subNode.id);
        }
    }

    return {
        flowNodes: workNodes,
        connections: workConns,
        expansionFindings
    };
}

export function buildGenerationGraphIrFromContext(context) {
    const flatResult = flattenFlowGraphs(context);
    const flatContext = {
        ...context,
        flowNodes: flatResult.flowNodes,
        connections: flatResult.connections
    };

    const { nodesById, connections, connectionsByFrom } = buildGraphMaps(flatContext);
    const motionLookup = buildMotionLookup(flatContext);
    const startNodes = asArray(flatContext?.flowNodes).filter((node) => node?.type === "start");
    const startNode = startNodes[0] || null;
    const reachableNodeIds = enumerateReachableNodeIds(startNode?.id || "", connectionsByFrom);
    const orderedReachableIds = [...reachableNodeIds];
    const stepByNodeId = new Map(
        orderedReachableIds.map((nodeId, index) => [nodeId, nextGenerationStepNumber(index)])
    );
    const subprocessById = new Map(
        asArray(flatContext?.subprocesses).map((item) => [String(item?.id || ""), item])
    );

    const actuators = asArray(context?.actuators);
    const signals = asArray(context?.signals);
    const controlSpecFindings = [];

    const actuatorById = new Map();
    for (const act of actuators) {
        if (act?.id) {
            if (actuatorById.has(act.id)) {
                controlSpecFindings.push({
                    severity: "error",
                    code: "ACTUATOR_DUPLICATE",
                    message: `Duplicate actuator ID "${act.id}" detected in project actuators list.`,
                    actuatorId: act.id
                });
            } else {
                actuatorById.set(act.id, act);
            }
        }
    }

    const signalById = new Map();
    for (const sig of signals) {
        if (sig?.id) {
            if (signalById.has(sig.id)) {
                controlSpecFindings.push({
                    severity: "error",
                    code: "SIGNAL_DUPLICATE",
                    message: `Duplicate signal ID "${sig.id}" detected in project signals list.`,
                    signalId: sig.id
                });
            } else {
                signalById.set(sig.id, sig);
            }
        }
    }

    const steps = orderedReachableIds
        .map((nodeId) => nodesById.get(nodeId))
        .filter(Boolean)
        .map((rawNode) => {
            const node = rawNode.type === "motion" ? enrichMotionNode(rawNode, motionLookup) : rawNode;
            const step = stepByNodeId.get(node.id);
            const base = {
                step,
                nodeId: node.id,
                type: node.type,
                name: node.name || node.label || "",
                nodePath: node.nodePath || `Home:${node.id}`,
                transitions: {}
            };

            if (node.type === "motion") {
                const axis = node.motionRef?.axis || null;
                const motion = node.motionRef?.motion || null;
                const baseStep = {
                    ...base,
                    axisId: node.axisId || "",
                    motionId: node.motionId || "",
                    axisName: node.axisName || axis?.name || "",
                    motionName: node.motionName || motion?.name || "",
                    axis,
                    motion,
                    commandName: toStableStIdentifier(
                        `${node.axisName || axis?.name || "Axis"}_${node.motionName || motion?.name || "Motion"}`,
                        `Step${step}_Motion`,
                        5
                    ),
                    doneCondition: toStableStIdentifier(
                        `${node.axisName || axis?.name || "Axis"}_${node.motionName || motion?.name || "Motion"}_Done`,
                        `Step${step}_Done`,
                        8
                    ),
                    transitions: buildTransitionsForNode(node, connectionsByFrom, stepByNodeId)
                };

                if (node.actuatorId) {
                    const actuator = actuatorById.get(node.actuatorId);
                    if (!actuator) {
                        controlSpecFindings.push({
                            severity: "error",
                            code: "ACTUATOR_MISSING",
                            message: `Actuator "${node.actuatorId}" is not defined.`,
                            nodeId: node.id,
                            actuatorId: node.actuatorId
                        });
                    } else {
                        baseStep.actuator = {
                            id: actuator.id,
                            name: actuator.name || "",
                            actuatorType: actuator.actuatorType || "",
                            controlMethod: actuator.controlMethod || ""
                        };
                        if (actuator.axisRef) {
                            baseStep.actuator.axisRef = actuator.axisRef;
                        }

                        if (node.actionId) {
                            const action = asArray(actuator.actions).find(a => a.id === node.actionId);
                            if (!action) {
                                controlSpecFindings.push({
                                    severity: "error",
                                    code: "ACTION_MISSING",
                                    message: `Action "${node.actionId}" is not defined in actuator "${actuator.id}".`,
                                    nodeId: node.id,
                                    actuatorId: actuator.id,
                                    actionId: node.actionId
                                });
                            } else {
                                baseStep.action = {
                                    id: action.id,
                                    name: action.name || ""
                                };
                                if (action.plcopen?.fbName || action.plcopen?.family) {
                                    baseStep.action.plcopen = action.plcopen;
                                    baseStep.action.command = action.command || {};
                                    baseStep.action.parameters = action.parameters || {};
                                    baseStep.action.status = action.status || {};
                                }

                                const commands = [];
                                const setOutputs = asArray(action.command?.setOutputs);
                                const resetOutputs = asArray(action.command?.resetOutputs);

                                const isDiscreteIoActuator =
                                    actuator.controlMethod === "discreteIo" &&
                                    ["cylinderIo", "motorIo", "pneumaticCylinder", "motor"].includes(actuator.actuatorType);
                                const completionMode = getDiscreteIoCompletionMode(action);

                                if (isDiscreteIoActuator && setOutputs.length === 0 && resetOutputs.length === 0) {
                                    controlSpecFindings.push({
                                        severity: "error",
                                        code: "COMMAND_MISSING",
                                        message: `discreteIo action "${action.id}" has no command outputs.`,
                                        nodeId: node.id,
                                        actuatorId: actuator.id,
                                        actionId: action.id
                                    });
                                }

                                for (const sigId of setOutputs) {
                                    if (!sigId || !sigId.trim()) {
                                        controlSpecFindings.push({
                                            severity: "error",
                                            code: "SIGNAL_ID_MISSING",
                                            message: `Command output has a blank signal ID in actuator "${actuator.id}" action "${action.id}".`,
                                            nodeId: node.id,
                                            actuatorId: actuator.id,
                                            actionId: action.id
                                        });
                                        continue;
                                    }
                                    const sig = signalById.get(sigId);
                                    if (sig && sig.direction !== "output") {
                                        controlSpecFindings.push({
                                            severity: "error",
                                            code: "SIGNAL_DIRECTION_MISMATCH",
                                            message: `Signal "${sigId}" used in setOutputs command but its direction is not "output" (found "${sig.direction}").`,
                                            nodeId: node.id,
                                            actuatorId: actuator.id,
                                            actionId: action.id,
                                            signalId: sigId
                                        });
                                    }
                                    commands.push({ type: "setOutput", signalId: sigId });
                                }
                                for (const sigId of resetOutputs) {
                                    if (!sigId || !sigId.trim()) {
                                        controlSpecFindings.push({
                                            severity: "error",
                                            code: "SIGNAL_ID_MISSING",
                                            message: `Command output has a blank signal ID in actuator "${actuator.id}" action "${action.id}".`,
                                            nodeId: node.id,
                                            actuatorId: actuator.id,
                                            actionId: action.id
                                        });
                                        continue;
                                    }
                                    const sig = signalById.get(sigId);
                                    if (sig && sig.direction !== "output") {
                                        controlSpecFindings.push({
                                            severity: "error",
                                            code: "SIGNAL_DIRECTION_MISMATCH",
                                            message: `Signal "${sigId}" used in resetOutputs command but its direction is not "output" (found "${sig.direction}").`,
                                            nodeId: node.id,
                                            actuatorId: actuator.id,
                                            actionId: action.id,
                                            signalId: sigId
                                        });
                                    }
                                    commands.push({ type: "resetOutput", signalId: sigId });
                                }
                                baseStep.commands = commands;

                                const doneConditions = asArray(action.doneConditions);
                                if (isDiscreteIoActuator && completionMode === "sensor" && doneConditions.length === 0) {
                                    controlSpecFindings.push({
                                        severity: "error",
                                        code: "DONE_CONDITION_MISSING",
                                        message: `discreteIo action "${action.id}" has no done conditions.`,
                                        nodeId: node.id,
                                        actuatorId: actuator.id,
                                        actionId: action.id
                                    });
                                }
                                baseStep.doneConditions = doneConditions.flatMap(cond => {
                                    if (!cond.signalId || !cond.signalId.trim()) {
                                        controlSpecFindings.push({
                                            severity: "error",
                                            code: "SIGNAL_ID_MISSING",
                                            message: `Done condition has a blank signal ID in actuator "${actuator.id}" action "${action.id}".`,
                                            nodeId: node.id,
                                            actuatorId: actuator.id,
                                            actionId: action.id
                                        });
                                        return [];
                                    }
                                    const sig = signalById.get(cond.signalId);
                                    if (sig && sig.direction !== "input" && sig.direction !== "internal") {
                                        controlSpecFindings.push({
                                            severity: "error",
                                            code: "SIGNAL_DIRECTION_MISMATCH",
                                            message: `Signal "${cond.signalId}" used in doneConditions but its direction is not "input" or "internal" (found "${sig.direction}").`,
                                            nodeId: node.id,
                                            actuatorId: actuator.id,
                                            actionId: action.id,
                                            signalId: cond.signalId
                                        });
                                    }
                                    return [{
                                        signalId: cond.signalId || "",
                                        operator: cond.operator || "==",
                                        value: cond.value !== undefined ? cond.value : true
                                    }];
                                });
                                baseStep.completionMode = completionMode;

                                baseStep.faultConditions = asArray(action.faultConditions).flatMap(cond => {
                                    if (!cond.signalId || !cond.signalId.trim()) {
                                        controlSpecFindings.push({
                                            severity: "error",
                                            code: "SIGNAL_ID_MISSING",
                                            message: `Fault condition has a blank signal ID in actuator "${actuator.id}" action "${action.id}".`,
                                            nodeId: node.id,
                                            actuatorId: actuator.id,
                                            actionId: action.id
                                        });
                                        return [];
                                    }
                                    const sig = signalById.get(cond.signalId);
                                    if (sig && sig.direction !== "input" && sig.direction !== "internal") {
                                        controlSpecFindings.push({
                                            severity: "error",
                                            code: "SIGNAL_DIRECTION_MISMATCH",
                                            message: `Signal "${cond.signalId}" used in faultConditions but its direction is not "input" or "internal" (found "${sig.direction}").`,
                                            nodeId: node.id,
                                            actuatorId: actuator.id,
                                            actionId: action.id,
                                            signalId: cond.signalId
                                        });
                                    }
                                    return [{
                                        signalId: cond.signalId || "",
                                        operator: cond.operator || "==",
                                        value: cond.value !== undefined ? cond.value : true
                                    }];
                                });

                                if (isDiscreteIoActuator && completionMode === "sensor" && (action.timeoutSec == null || action.timeoutSec === 0)) {
                                    controlSpecFindings.push({
                                        severity: "warning",
                                        code: "TIMEOUT_MISSING",
                                        message: `discreteIo action "${action.id}" has no timeout specified.`,
                                        nodeId: node.id,
                                        actuatorId: actuator.id,
                                        actionId: action.id
                                    });
                                }
                                baseStep.timeoutSec = numOrZero(action.timeoutSec);
                            }
                        } else {
                            controlSpecFindings.push({
                                severity: "error",
                                code: "ACTION_MISSING",
                                message: `Node has actuatorId but missing actionId.`,
                                nodeId: node.id,
                                actuatorId: node.actuatorId
                            });
                        }
                    }
                }

                return baseStep;
            }

            if (node.type === "condition") {
                const cond = buildConditionExpression(node, step);
                return {
                    ...base,
                    conditionText: node.conditionText || node.name || "条件",
                    conditionMode: node.conditionMode || "single",
                    operator: node.operator || "AND",
                    conditions: asArray(node.conditions),
                    ...(node.conditionKind ? {
                        conditionKind: node.conditionKind,
                        conditionName: node.conditionName || "",
                        conditionValue: node.conditionValue === false ? false : true,
                        conditionActuatorId: node.conditionActuatorId || "",
                        conditionSignalId: node.conditionSignalId || ""
                    } : {}),
                    conditionExpression: cond.expression,
                    conditionTerms: cond.terms,
                    timerName: toStableStIdentifier(node.conditionText || `Condition_${step}`, `CondTimer_${step}`, 5),
                    trueLabel: node.trueLabel || "ON",
                    falseLabel: node.falseLabel || "OFF",
                    timeoutLabel: node.timeoutLabel || "TIMEOUT",
                    timeoutSec: numOrZero(node.timeoutSec),
                    transitions: buildTransitionsForNode(node, connectionsByFrom, stepByNodeId)
                };
            }

            if (node.type === "button") {
                const button = buildButtonExpression(node, step);
                return {
                    ...base,
                    signalName: node.signalName || "",
                    mode: button.mode,
                    trueLabel: node.trueLabel || "ON",
                    falseLabel: node.falseLabel || "OFF",
                    signalIdent: button.signalIdent,
                    edgeIdent: button.edgeIdent,
                    latchIdent: button.latchIdent,
                    transitions: buildTransitionsForNode(node, connectionsByFrom, stepByNodeId)
                };
            }

            if (node.type === "signal_output") {
                return {
                    ...base,
                    signalScope: node.signalScope || "external",
                    signalName: node.signalName || "",
                    action: node.action || "ON",
                    signalIdent: toStableStIdentifier(node.signalName || node.name || `Signal_${step}`, `Signal_${step}`, 5),
                    transitions: buildTransitionsForNode(node, connectionsByFrom, stepByNodeId)
                };
            }

            if (node.type === "wait") {
                return {
                    ...base,
                    label: node.label || node.name || "待機",
                    durationSec: numOrZero(node.durationSec ?? 1),
                    timerName: toStableStIdentifier(node.label || `Wait_${step}`, `WaitTimer_${step}`, 5),
                    transitions: buildTransitionsForNode(node, connectionsByFrom, stepByNodeId)
                };
            }

            if (node.type === "subprocess") {
                const definition = subprocessById.get(String(node.subprocessId || ""));
                return {
                    ...base,
                    subprocessId: node.subprocessId || "",
                    subprocessName: node.subprocessName || node.name || "サブプロセス",
                    moduleName: definition?.moduleName || "",
                    durationSec: numOrZero(node.durationSec ?? definition?.durationSec ?? 1),
                    timerName: toStableStIdentifier(node.subprocessName || `Subprocess_${step}`, `SubTimer_${step}`, 5),
                    transitions: buildTransitionsForNode(node, connectionsByFrom, stepByNodeId)
                };
            }

            if (node.type === "error") {
                return {
                    ...base,
                    errorCode: node.errorCode || "E001",
                    errorMessage: node.errorMessage || "エラー",
                    errorIdent: toStableStIdentifier(node.errorCode || `Error_${step}`, `Error_${step}`, 5),
                    transitions: {}
                };
            }

            if (node.type === "end") {
                return {
                    ...base,
                    label: node.label || "終了",
                    result: node.result || "normal",
                    transitions: buildTransitionsForNode(node, connectionsByFrom, stepByNodeId)
                };
            }

            return {
                ...base,
                label: node.label || node.name || "",
                transitions: buildTransitionsForNode(node, connectionsByFrom, stepByNodeId)
            };
        });

    const referencedSignalIds = new Set();
    const referencedSignals = [];

    for (const s of steps) {
        if (s.signalName) {
            referencedSignalIds.add(s.signalName);
        }
        if (s.commands) {
            for (const cmd of s.commands) {
                if (cmd.signalId) referencedSignalIds.add(cmd.signalId);
            }
        }
        if (s.doneConditions) {
            for (const cond of s.doneConditions) {
                if (cond.signalId) referencedSignalIds.add(cond.signalId);
            }
        }
        if (s.faultConditions) {
            for (const cond of s.faultConditions) {
                if (cond.signalId) referencedSignalIds.add(cond.signalId);
            }
        }
    }

    for (const sigId of referencedSignalIds) {
        const sig = signalById.get(sigId);
        if (!sig) {
            let refNodeId = "";
            let refActId = "";
            let refActIdAct = "";
            for (const s of steps) {
                const hasCmd = s.commands?.some(c => c.signalId === sigId);
                const hasDone = s.doneConditions?.some(c => c.signalId === sigId);
                const hasFault = s.faultConditions?.some(c => c.signalId === sigId);
                if (hasCmd || hasDone || hasFault) {
                    refNodeId = s.nodeId;
                    refActId = s.actuator?.id || "";
                    refActIdAct = s.action?.id || "";
                    break;
                }
            }
            controlSpecFindings.push({
                severity: "error",
                code: "SIGNAL_MISSING",
                message: `Signal "${sigId}" is not defined.`,
                nodeId: refNodeId || undefined,
                actuatorId: refActId || undefined,
                actionId: refActIdAct || undefined,
                signalId: sigId
            });
        } else {
            referencedSignals.push(sig);
        }
    }

    const ioTable = [];
    for (const sig of referencedSignals) {
        if ((sig.direction === "input" || sig.direction === "output") && (!sig.address || !sig.address.trim())) {
            let refNodeId = "";
            let refActId = "";
            let refActIdAct = "";
            for (const s of steps) {
                const hasCmd = s.commands?.some(c => c.signalId === sig.id);
                const hasDone = s.doneConditions?.some(c => c.signalId === sig.id);
                const hasFault = s.faultConditions?.some(c => c.signalId === sig.id);
                if (hasCmd || hasDone || hasFault) {
                    refNodeId = s.nodeId;
                    refActId = s.actuator?.id || "";
                    refActIdAct = s.action?.id || "";
                    break;
                }
            }
            controlSpecFindings.push({
                severity: "warning",
                code: "SIGNAL_ADDRESS_MISSING",
                message: `Signal "${sig.id}" requires an address for I/O mapping.`,
                nodeId: refNodeId || undefined,
                actuatorId: refActId || undefined,
                actionId: refActIdAct || undefined,
                signalId: sig.id
            });
        }

        const referencedBy = [];
        const usagesSet = new Set();

        for (const s of steps) {
            if (s.signalName === sig.id) {
                referencedBy.push({
                    step: s.step,
                    nodeId: s.nodeId,
                    label: s.name || s.label || "",
                    usage: s.type === "button" ? "buttonSignal" : "signalOutput"
                });
                usagesSet.add(s.type === "button" ? "buttonSignal" : "signalOutput");
            }

            const cmdMatch = s.commands?.find(c => c.signalId === sig.id);
            if (cmdMatch) {
                referencedBy.push({
                    step: s.step,
                    nodeId: s.nodeId,
                    label: s.name || s.label || "",
                    usage: cmdMatch.type
                });
                usagesSet.add(cmdMatch.type);
            }

            const doneMatch = s.doneConditions?.find(c => c.signalId === sig.id);
            if (doneMatch) {
                referencedBy.push({
                    step: s.step,
                    nodeId: s.nodeId,
                    label: s.name || s.label || "",
                    usage: "doneCondition"
                });
                usagesSet.add("doneCondition");
            }

            const faultMatch = s.faultConditions?.find(c => c.signalId === sig.id);
            if (faultMatch) {
                referencedBy.push({
                    step: s.step,
                    nodeId: s.nodeId,
                    label: s.name || s.label || "",
                    usage: "faultCondition"
                });
                usagesSet.add("faultCondition");
            }
        }

        const ioEntry = {
            direction: sig.direction || "input",
            address: sig.address || "",
            signalId: sig.id,
            signalName: sig.name || "",
            usages: [...usagesSet],
            referencedBy: referencedBy
        };
        if (sig.iecName) ioEntry.iecName = sig.iecName;
        if (sig.dataType && sig.dataType !== "BOOL") ioEntry.dataType = sig.dataType;
        ioTable.push(ioEntry);
    }

    return {
        contractVersion: GENERATION_IR_VERSION,
        flowContextVersion: context?.contractVersion,
        dialect: context?.dialect,
        vendorTarget: context?.vendorTarget,
        plcProfileId: context?.plcProfileId || "",
        plcProfileName: context?.plcProfileName || "",
        plcProfilePromptRules: context?.plcProfilePromptRules || "",
        summary: context?.summary,
        startNodeId: startNode?.id || "",
        startStep: startNode ? stepByNodeId.get(startNode.id) || 0 : 0,
        steps,
        connections,
        reachableNodeIds: orderedReachableIds,
        conditions: context?.conditions || [],
        errors: context?.errors || [],
        expansionFindings: flatResult.expansionFindings,

        actuators: actuators,
        signals: referencedSignals,
        ioTable: ioTable,
        controlSpecFindings: controlSpecFindings
    };
}

export function buildGenerationIrFromContext(context) {
    return buildGenerationGraphIrFromContext(context);
}
